import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileSpreadsheet, Plus, RefreshCw, Save, Search, Upload, X } from 'lucide-react';
import { db } from '../../firebase/config';
import { collection, doc, getDocs, serverTimestamp, writeBatch } from 'firebase/firestore';

const OFFICE_OPTIONS = [
  { value: '', label: 'No Office' },
  { value: 'classRepresentative', label: 'Class Representative' },
  { value: 'boysRepresentative', label: 'Boys Representative', gender: 'Male' },
  { value: 'girlsRepresentative', label: 'Girls Representative', gender: 'Female' },
  { value: 'sportsRepresentative', label: 'Sports Representative' },
  { value: 'culturalRepresentative', label: 'Cultural Representative' }
];

const DEFAULT_BRANCH = 'Engineering Sciences';
const DEFAULT_SECURITY_PIN = '123456';

const BRANCH_OPTIONS = [
  DEFAULT_BRANCH,
  'Mechanical Engineering',
  'Electrical Engineering',
  'Computer Engineering',
  'Computer Science and Engineering',
  'AI&ML Engineering'
];

const blankStudent = {
  rollNumber: '',
  surname: '',
  firstName: '',
  fatherName: '',
  dob: '',
  gender: '',
  branch: DEFAULT_BRANCH,
  division: '',
  batch: '',
  currentAddress: '',
  permanentAddress: '',
  aadharCard: '',
  bloodGroup: '',
  abcId: '',
  hobbies: '',
  mobile: '',
  whatsapp: '',
  email: '',
  fatherMobile: '',
  fatherOccupation: '',
  motherName: '',
  motherMobile: '',
  motherOccupation: '',
  officeRole: '',
  securityPin: ''
};

const FIELD_ALIASES = {
  rollNumber: ['rollnumber', 'rollno', 'roll', 'studentrollnumber'],
  surname: ['surname', 'lastname', 'secondname'],
  firstName: ['firstname', 'first', 'name', 'studentname'],
  fatherName: ['fathername', 'fathersname', 'middlename'],
  dob: ['dob', 'dateofbirth', 'birthdate'],
  gender: ['gender', 'sex'],
  branch: ['branch', 'program'],
  division: ['division', 'div'],
  batch: ['batch'],
  currentAddress: ['currentaddress', 'address', 'localaddress'],
  permanentAddress: ['permanentaddress', 'homeaddress'],
  aadharCard: ['aadharcard', 'aadhaarcard', 'aadhar', 'aadhaar'],
  bloodGroup: ['bloodgroup', 'blood'],
  abcId: ['abcid', 'abc'],
  hobbies: ['hobbies', 'interest', 'interests'],
  mobile: ['mobile', 'mobilenumber', 'studentmobile', 'phone'],
  whatsapp: ['whatsapp', 'whatsappnumber'],
  email: ['email', 'emailid', 'studentemail'],
  fatherMobile: ['fathermobile', 'fathermobilenumber', 'fatherphone'],
  fatherOccupation: ['fatheroccupation', 'fathersoccupation'],
  motherName: ['mothername', 'mothersname'],
  motherMobile: ['mothermobile', 'mothermobilenumber', 'motherphone'],
  motherOccupation: ['motheroccupation', 'mothersoccupation']
};

const normalizeKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const normalizeRoll = (value) => String(value || '').trim().toUpperCase();
const officeLabel = (role) => OFFICE_OPTIONS.find((option) => option.value === role)?.label || '';

const cleanImportValue = (value) => {
  const cleaned = String(value ?? '').trim();
  if (!cleaned || cleaned === '-' || cleaned.toLowerCase() === 'na' || cleaned.toLowerCase() === 'n/a') return '';
  return cleaned;
};

const excelSerialToDate = (serial) => {
  const utcDays = Math.floor(Number(serial) - 25569);
  const date = new Date(utcDays * 86400 * 1000);
  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const normalizeDate = (value) => {
  if (typeof value === 'number') return excelSerialToDate(value);

  const cleaned = cleanImportValue(value).replace(/\//g, '-');
  const match = cleaned.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (!match) return cleaned;

  return `${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}-${match[3]}`;
};

const normalizeGender = (value) => {
  const gender = String(value || '').trim().toLowerCase();
  if (gender.startsWith('f')) return 'Female';
  if (gender.startsWith('m')) return 'Male';
  return value || '';
};

const normalizeImportRow = (row) => {
  const normalizedRow = {};
  Object.entries(row || {}).forEach(([key, value]) => {
    normalizedRow[normalizeKey(key)] = value;
  });
  return normalizedRow;
};

const getImportAliasMatch = (normalizedRow, field) => {
  const aliases = FIELD_ALIASES[field] || [field];
  return aliases.find((alias) => normalizedRow[alias] !== undefined);
};

const getRawMappedValue = (normalizedRow, field) => {
  const match = getImportAliasMatch(normalizedRow, field);
  return match ? normalizedRow[match] : '';
};

const getMappedValue = (normalizedRow, field) => {
  return cleanImportValue(getRawMappedValue(normalizedRow, field));
};

const studentName = (student) => `${student.firstName || ''} ${student.surname || ''}`.trim() || student.rollNumber;

const cloneOffices = (officeAssignments) => {
  const next = {};
  Object.entries(officeAssignments || {}).forEach(([division, data]) => {
    next[division] = {
      division,
      offices: { ...(data.offices || {}) }
    };
  });
  return next;
};

const findOfficeForStudent = (officeAssignments, rollNumber) => {
  const roll = normalizeRoll(rollNumber);
  for (const [division, data] of Object.entries(officeAssignments || {})) {
    for (const [role, holder] of Object.entries(data.offices || {})) {
      if (normalizeRoll(holder?.rollNumber) === roll) {
        return { division, role, label: officeLabel(role) };
      }
    }
  }
  return null;
};

const updateOfficeState = (officeAssignments, student, nextRole, clearWhenEmpty = true) => {
  const rollNumber = normalizeRoll(student.rollNumber);
  const division = student.division;
  const roleConfig = OFFICE_OPTIONS.find((option) => option.value === nextRole);
  const next = cloneOffices(officeAssignments);
  const touched = new Set();

  Object.entries(next).forEach(([divisionKey, data]) => {
    Object.entries(data.offices || {}).forEach(([role, holder]) => {
      if (normalizeRoll(holder?.rollNumber) === rollNumber) {
        delete data.offices[role];
        touched.add(divisionKey);
      }
    });
  });

  if (!nextRole) {
    return { next, touched: clearWhenEmpty ? touched : new Set() };
  }

  if (!division) {
    throw new Error(`${rollNumber}: select a division before assigning an office.`);
  }

  if (roleConfig?.gender && normalizeGender(student.gender) !== roleConfig.gender) {
    throw new Error(`${rollNumber}: ${roleConfig.label} must be ${roleConfig.gender}.`);
  }

  const divisionRecord = next[division] || { division, offices: {} };
  const existingHolder = divisionRecord.offices[nextRole];
  if (existingHolder && normalizeRoll(existingHolder.rollNumber) !== rollNumber) {
    throw new Error(`${roleConfig.label} for Division ${division} is already assigned to ${existingHolder.name || existingHolder.rollNumber}.`);
  }

  divisionRecord.offices[nextRole] = {
    rollNumber,
    name: studentName(student),
    gender: normalizeGender(student.gender),
    assignedAt: new Date().toISOString()
  };
  next[division] = divisionRecord;
  touched.add(division);

  return { next, touched };
};

const getOptionValues = (docs, fieldNames) => {
  const values = new Set();
  docs.forEach((snapshot) => {
    const data = snapshot.data();
    fieldNames.forEach((field) => {
      if (data[field]) values.add(data[field]);
    });
    if (snapshot.id) values.add(snapshot.id);
  });
  return values;
};

const StudentManagementWidget = () => {
  const fileInputRef = useRef(null);
  const [students, setStudents] = useState([]);
  const [selectedRoll, setSelectedRoll] = useState('');
  const [formData, setFormData] = useState(blankStudent);
  const [divisions, setDivisions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [officeAssignments, setOfficeAssignments] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const isNew = selectedRoll === '__new__';

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;

    return students.filter((student) => (
      (student.rollNumber || '').toLowerCase().includes(term) ||
      (student.name || '').toLowerCase().includes(term) ||
      `${student.firstName || ''} ${student.surname || ''}`.toLowerCase().includes(term)
    ));
  }, [search, students]);

  const toFormData = (student = {}, offices = officeAssignments) => {
    const office = findOfficeForStudent(offices, student.rollNumber);
    return {
      ...blankStudent,
      ...student,
      rollNumber: normalizeRoll(student.rollNumber || ''),
      gender: normalizeGender(student.gender),
      officeRole: office?.role || student.officeRole || '',
      securityPin: student.securityPin || ''
    };
  };

  const loadData = async () => {
    setLoading(true);
    setMessage('');

    try {
      const [studentsSnap, divisionSnap, batchSnap, officeSnap, pinSnap] = await Promise.all([
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'divisionAssignments')),
        getDocs(collection(db, 'batchAssignments')),
        getDocs(collection(db, 'studentOffices')),
        getDocs(collection(db, 'secupin'))
      ]);

      const divisionSet = getOptionValues(divisionSnap.docs, ['division', 'name']);
      const batchSet = getOptionValues(batchSnap.docs, ['batch', 'name']);
      const officeMap = {};
      const pinMap = {};

      officeSnap.docs.forEach((officeDoc) => {
        officeMap[officeDoc.id] = {
          division: officeDoc.id,
          offices: officeDoc.data().offices || {}
        };
      });

      pinSnap.docs.forEach((pinDoc) => {
        const data = pinDoc.data();
        const rollNumber = normalizeRoll(data.rollNumber || pinDoc.id);
        pinMap[rollNumber] = data.pin || DEFAULT_SECURITY_PIN;
      });

      const rows = studentsSnap.docs
        .map((studentDoc) => {
          const data = { id: studentDoc.id, ...studentDoc.data() };
          const rollNumber = normalizeRoll(data.rollNumber || studentDoc.id);
          if (data.division) divisionSet.add(data.division);
          if (data.batch) batchSet.add(data.batch);
          const office = findOfficeForStudent(officeMap, rollNumber);
          return {
            ...data,
            rollNumber,
            officeRole: office?.role || data.officeRole || '',
            officeLabel: office?.label || data.officeLabel || '',
            securityPin: pinMap[rollNumber] || DEFAULT_SECURITY_PIN,
            hasSecurityPinRecord: Boolean(pinMap[rollNumber])
          };
        })
        .sort((a, b) => a.rollNumber.localeCompare(b.rollNumber, undefined, { numeric: true }));

      setStudents(rows);
      setDivisions([...divisionSet].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
      setBatches([...batchSet].filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })));
      setOfficeAssignments(officeMap);

      if (isNew) return;

      const nextRoll = rows.some((student) => student.rollNumber === selectedRoll)
        ? selectedRoll
        : rows[0]?.rollNumber || '';
      setSelectedRoll(nextRoll);
      setFormData(toFormData(rows.find((student) => student.rollNumber === nextRoll) || rows[0] || {}, officeMap));
    } catch (error) {
      console.error('Error loading student management data:', error);
      setMessage('Could not load student management data.');
    } finally {
      setLoading(false);
    }
  };

  const startNewStudent = () => {
    setSelectedRoll('__new__');
    setFormData(blankStudent);
    setMessage('');
  };

  const selectStudent = (student) => {
    setSelectedRoll(student.rollNumber);
    setFormData(toFormData(student));
    setMessage('');
  };

  const updateForm = (field, value) => {
    setFormData((current) => ({
      ...current,
      [field]: field === 'rollNumber' ? normalizeRoll(value) : value
    }));
  };

  const validateStudent = (student) => {
    if (!student.rollNumber || !student.firstName || !student.surname || !student.dob) {
      throw new Error('Roll number, first name, surname, and DOB are required.');
    }
    if (!student.division) {
      throw new Error('Division is required.');
    }
    if (student.securityPin && !/^\d{6}$/.test(student.securityPin)) {
      throw new Error('Security PIN must be exactly 6 digits.');
    }
  };

  const buildStudentPayload = (source) => {
    const rollNumber = normalizeRoll(source.rollNumber);
    return {
      rollNumber,
      surname: source.surname || '',
      firstName: source.firstName || '',
      fatherName: source.fatherName || '',
      name: `${source.firstName || ''} ${source.surname || ''}`.trim(),
      dob: source.dob || '',
      gender: normalizeGender(source.gender),
      branch: source.branch || DEFAULT_BRANCH,
      department: source.department || 'Engineering Sciences & Humanities',
      division: source.division || '',
      batch: source.batch || '',
      currentAddress: source.currentAddress || '',
      permanentAddress: source.permanentAddress || '',
      aadharCard: source.aadharCard || '',
      bloodGroup: source.bloodGroup || '',
      abcId: source.abcId || '',
      hobbies: source.hobbies || '',
      mobile: source.mobile || '',
      whatsapp: source.whatsapp || '',
      email: source.email || '',
      fatherMobile: source.fatherMobile || '',
      fatherOccupation: source.fatherOccupation || '',
      motherName: source.motherName || '',
      motherMobile: source.motherMobile || '',
      motherOccupation: source.motherOccupation || '',
      officeRole: source.officeRole || '',
      officeLabel: officeLabel(source.officeRole),
      updatedAt: serverTimestamp()
    };
  };

  const writeOfficeDocs = (batch, nextOfficeAssignments, touched) => {
    touched.forEach((division) => {
      batch.set(doc(db, 'studentOffices', division), {
        division,
        offices: nextOfficeAssignments[division]?.offices || {},
        updatedAt: serverTimestamp()
      }, { merge: true });
    });
  };

  const saveStudent = async () => {
    setSaving(true);
    setMessage('');

    try {
      validateStudent(formData);
      const student = buildStudentPayload(formData);
      const batch = writeBatch(db);
      const { next, touched } = updateOfficeState(officeAssignments, student, formData.officeRole, true);

      batch.set(doc(db, 'students', student.rollNumber), student, { merge: !isNew });

      if (isNew || formData.securityPin) {
        const pin = formData.securityPin || DEFAULT_SECURITY_PIN;
        batch.set(doc(db, 'secupin', student.rollNumber), {
          rollNumber: student.rollNumber,
          pin,
          isDefault: pin === DEFAULT_SECURITY_PIN,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      writeOfficeDocs(batch, next, touched);
      await batch.commit();

      setOfficeAssignments(next);
      setMessage('Student saved.');
      await loadData();
      setSelectedRoll(student.rollNumber);
    } catch (error) {
      console.error('Error saving student:', error);
      setMessage(error.message || 'Could not save student.');
    } finally {
      setSaving(false);
    }
  };

  const mapImportRow = (row) => {
    const mapped = { ...blankStudent, branch: '', batch: '', officeRole: '', securityPin: '' };
    const normalizedRow = normalizeImportRow(row);
    const presentFields = new Set();

    Object.keys(FIELD_ALIASES).forEach((field) => {
      if (getImportAliasMatch(normalizedRow, field)) {
        presentFields.add(field);
        mapped[field] = getMappedValue(normalizedRow, field);
      }
    });

    mapped.rollNumber = normalizeRoll(mapped.rollNumber);
    mapped.gender = normalizeGender(mapped.gender);
    mapped.dob = normalizeDate(presentFields.has('dob') ? getRawMappedValue(normalizedRow, 'dob') : mapped.dob);
    mapped.division = mapped.division || mapped.rollNumber.charAt(1);
    mapped.batch = '';
    mapped.branch = mapped.branch || '';
    mapped.officeRole = '';
    mapped.securityPin = '';
    mapped.presentFields = presentFields;
    return mapped;
  };

  const parseImportFile = async (file) => {
    if (file.name.toLowerCase() !== 'studentlist.xlsx') {
      throw new Error('Upload the official file named studentlist.xlsx.');
    }

    const extension = file.name.split('.').pop().toLowerCase();
    if (extension !== 'xlsx') throw new Error('studentlist.xlsx must be an Excel .xlsx file.');

    const XLSX = await import('xlsx');
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { defval: '' });
  };

  const importStudents = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setMessage('');

    try {
      const rows = await parseImportFile(file);
      const batch = writeBatch(db);
      let nextOfficeAssignments = cloneOffices(officeAssignments);
      const touchedDivisions = new Set();
      const existingStudents = new Map(students.map((student) => [normalizeRoll(student.rollNumber), student]));
      let imported = 0;
      const skipped = [];

      rows.forEach((row, index) => {
        try {
          const mapped = mapImportRow(row);
          const existingStudent = existingStudents.get(mapped.rollNumber) || {};
          const fromSheetOrExisting = (field, fallback = '') => (
            mapped.presentFields.has(field) ? mapped[field] : existingStudent[field] || fallback
          );
          const inferredDivision = mapped.rollNumber.charAt(1);
          const source = {
            rollNumber: mapped.rollNumber,
            surname: fromSheetOrExisting('surname'),
            firstName: fromSheetOrExisting('firstName'),
            fatherName: fromSheetOrExisting('fatherName'),
            dob: fromSheetOrExisting('dob'),
            gender: fromSheetOrExisting('gender'),
            division: mapped.presentFields.has('division') ? mapped.division || inferredDivision : existingStudent.division || inferredDivision,
            batch: existingStudent.batch || '',
            branch: fromSheetOrExisting('branch', DEFAULT_BRANCH),
            currentAddress: fromSheetOrExisting('currentAddress'),
            permanentAddress: fromSheetOrExisting('permanentAddress'),
            aadharCard: fromSheetOrExisting('aadharCard'),
            bloodGroup: fromSheetOrExisting('bloodGroup'),
            abcId: fromSheetOrExisting('abcId'),
            hobbies: fromSheetOrExisting('hobbies'),
            mobile: fromSheetOrExisting('mobile'),
            whatsapp: fromSheetOrExisting('whatsapp'),
            email: fromSheetOrExisting('email'),
            fatherMobile: fromSheetOrExisting('fatherMobile'),
            fatherOccupation: fromSheetOrExisting('fatherOccupation'),
            motherName: fromSheetOrExisting('motherName'),
            motherMobile: fromSheetOrExisting('motherMobile'),
            motherOccupation: fromSheetOrExisting('motherOccupation'),
            officeRole: existingStudent.officeRole || ''
          };
          validateStudent(source);
          const student = buildStudentPayload(source);

          if (source.officeRole) {
            const officeUpdate = updateOfficeState(nextOfficeAssignments, student, source.officeRole, false);
            nextOfficeAssignments = officeUpdate.next;
            officeUpdate.touched.forEach((division) => touchedDivisions.add(division));
          }

          batch.set(doc(db, 'students', student.rollNumber), student, { merge: true });
          if (!existingStudent.rollNumber || !existingStudent.hasSecurityPinRecord) {
            batch.set(doc(db, 'secupin', student.rollNumber), {
              rollNumber: student.rollNumber,
              pin: DEFAULT_SECURITY_PIN,
              isDefault: true,
              updatedAt: serverTimestamp()
            }, { merge: true });
          }
          imported += 1;
        } catch (error) {
          skipped.push(`Row ${index + 2}: ${error.message}`);
        }
      });

      writeOfficeDocs(batch, nextOfficeAssignments, touchedDivisions);
      await batch.commit();
      setOfficeAssignments(nextOfficeAssignments);
      await loadData();
      setMessage(`Imported ${imported} students.${skipped.length ? ` Skipped ${skipped.length}. ${skipped.slice(0, 3).join(' ')}` : ''}`);
    } catch (error) {
      console.error('Error importing students:', error);
      setMessage(error.message || 'Could not import students.');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const renderInput = (field, label, options = {}) => (
    <label className={options.full ? 'block md:col-span-2' : 'block'}>
      <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">{label}</span>
      <input
        type={options.type || 'text'}
        value={formData[field] || ''}
        onChange={(event) => updateForm(field, event.target.value)}
        disabled={options.disabled}
        placeholder={options.placeholder || ''}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
      />
    </label>
  );

  const renderSelect = (field, label, values, options = {}) => (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">{label}</span>
      <select
        value={formData[field] || ''}
        onChange={(event) => updateForm(field, event.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      >
        <option value="">{options.placeholder || `Select ${label.toLowerCase()}`}</option>
        {values.map((value) => (
          <option key={value.value || value} value={value.value || value}>
            {value.label || value}
          </option>
        ))}
      </select>
    </label>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-500">
        <RefreshCw className="mr-3 h-5 w-5 animate-spin" />
        Loading students
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(280px,380px)_1fr]">
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="space-y-3 border-b border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={startNewStudent}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:bg-gray-400"
            >
              {importing ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Import
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={importStudents}
            className="hidden"
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search students"
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="max-h-[620px] overflow-y-auto">
          {isNew && (
            <div className="border-b border-gray-100 bg-indigo-50 px-4 py-3">
              <div className="font-semibold text-gray-900">New Student</div>
              <div className="mt-1 text-xs text-gray-500">Unsaved</div>
            </div>
          )}

          {filteredStudents.map((student) => (
            <button
              key={student.rollNumber}
              type="button"
              onClick={() => selectStudent(student)}
              className={`block w-full border-b border-gray-100 px-4 py-3 text-left transition-colors ${
                selectedRoll === student.rollNumber ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'
              }`}
            >
              <div className="font-semibold text-gray-900">{student.rollNumber}</div>
              <div className="mt-1 truncate text-xs text-gray-500">{studentName(student)}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {student.division && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">Div {student.division}</span>}
                {student.batch && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">Batch {student.batch}</span>}
                {student.officeLabel && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{student.officeLabel}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-5 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {isNew ? 'New Student' : formData.rollNumber || 'Student'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">{studentName(formData)}</p>
          </div>
          <div className="flex gap-2">
            {isNew && (
              <button
                type="button"
                onClick={() => {
                  setSelectedRoll(students[0]?.rollNumber || '');
                  setFormData(toFormData(students[0] || {}));
                }}
                className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={saveStudent}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Student
            </button>
          </div>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-2">
          {renderInput('rollNumber', 'Roll Number', { disabled: !isNew })}
          {renderInput('dob', 'Date of Birth', { placeholder: 'DD-MM-YYYY' })}
          {renderInput('surname', 'Surname')}
          {renderInput('firstName', 'First Name')}
          {renderInput('fatherName', "Father's Name")}
          {renderSelect('gender', 'Gender', ['Male', 'Female', 'Other'])}
          {renderSelect('branch', 'Branch', BRANCH_OPTIONS)}
          {renderSelect('division', 'Division', divisions)}
          {renderSelect('batch', 'Batch', batches, { placeholder: 'Assign batch' })}
          {renderInput('securityPin', 'Security PIN', { placeholder: `Default ${DEFAULT_SECURITY_PIN}` })}
          {renderSelect('officeRole', 'Student Office', OFFICE_OPTIONS)}
          {renderInput('bloodGroup', 'Blood Group')}
          {renderInput('mobile', 'Mobile')}
          {renderInput('whatsapp', 'WhatsApp')}
          {renderInput('email', 'Email', { type: 'email', full: true })}
          {renderInput('currentAddress', 'Current Address', { full: true })}
          {renderInput('permanentAddress', 'Permanent Address', { full: true })}
          {renderInput('aadharCard', 'Aadhar Card')}
          {renderInput('abcId', 'ABC ID')}
          {renderInput('hobbies', 'Hobbies', { full: true })}
          {renderInput('fatherMobile', "Father's Mobile")}
          {renderInput('fatherOccupation', "Father's Occupation")}
          {renderInput('motherName', "Mother's Name")}
          {renderInput('motherMobile', "Mother's Mobile")}
          {renderInput('motherOccupation', "Mother's Occupation", { full: true })}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="mb-2 flex items-center text-sm font-semibold text-gray-700">
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            studentlist.xlsx Rules
          </div>
          <div className="text-xs text-gray-600">
            Required file name: studentlist.xlsx. Required columns: Roll Number, Surname, First Name, Father Name, DOB. Every row is read and matching roll numbers are updated. Division is inferred from the second roll-number character for new rows. Batch and student office are assigned by HOD in this widget. Security PIN is not read from Excel; new students default to 123456 and changed PINs are preserved.
          </div>
        </div>

        {message && (
          <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentManagementWidget;
